import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Html5Qrcode } from "html5-qrcode";

const QRCodeScanner = forwardRef(
  ({ onScanSuccess, onScanError, facingMode = "environment" }, ref) => {
    const qrRegionId = "qr-reader";
    const scannerRef = useRef(null);
    const hasStartedRef = useRef(false);

    useImperativeHandle(ref, () => ({
      stop: async () => {
        console.log("[Scanner] Manual stop called via ref");
        if (scannerRef.current && hasStartedRef.current) {
          try {
            await scannerRef.current.stop();
            await scannerRef.current.clear();
            hasStartedRef.current = false;
            console.log("[Scanner] Scanner stopped manually via ref");
          } catch (err) {
            console.warn(
              "[Scanner] Error stopping scanner via ref:",
              err.message
            );
          }
        } else {
          console.log("[Scanner] Skip stop — not started.");
        }
      },
    }));

    useEffect(() => {
      let isMounted = true;

      const startScanner = async () => {
        console.log("[Scanner] Starting scanner...");
        const container = document.getElementById(qrRegionId);
        if (container) container.innerHTML = "";

        if (scannerRef.current && hasStartedRef.current) {
          try {
            console.log("[Scanner] Cleaning up previous scanner...");
            await scannerRef.current.stop();
            await scannerRef.current.clear();
            hasStartedRef.current = false;
          } catch (err) {
            console.warn("[Scanner] Error cleaning scanner:", err.message);
          }
        }

        if (!scannerRef.current) {
          scannerRef.current = new Html5Qrcode(qrRegionId);
        }

        try {
          await scannerRef.current.start(
            { facingMode },
            { fps: 10, qrbox: 250 },
            async (decodedText) => {
              if (hasStartedRef.current && scannerRef.current) {
                try {
                  await scannerRef.current.stop();
                  await scannerRef.current.clear();
                  hasStartedRef.current = false;
                } catch {}
              }

              onScanSuccess(decodedText);
            },
            (errorMessage) => {
              if (onScanError) onScanError(errorMessage);
            }
          );

          hasStartedRef.current = true; 

          console.log("[Scanner] Scanner started.");
        } catch (err) {
          console.error("[Scanner] Scanner start error:", err.message);
        }
      };

      startScanner();

      return () => {
        isMounted = false;
        if (scannerRef.current && hasStartedRef.current) {
          scannerRef.current
            .stop()
            .then(() => {
              return scannerRef.current.clear();
            })
            .then(() => {
              hasStartedRef.current = false;
              console.log("[Scanner] Cleaned up on unmount");
            })
            .catch((err) => {
              console.warn("[Scanner] Unmount cleanup error:", err.message);
            });
        }
      };
    }, [facingMode, onScanSuccess, onScanError]);

    return (
      <div
        id={qrRegionId}
        style={{
          width: "100%",
          maxWidth: "400px",
          margin: "0 auto",
          padding: "10px",
        }}
      />
    );
  }
);

export default QRCodeScanner;
